// Charteris Phase 2.5 Task 5 (2026-10-07): the CHARTERIS reason vocabulary
// for the SI move card and the Signal tab subtitle. Mirrors
// signal_core/charteris_mt_signal.py: compute_si_m() m_reason codes
// (no_bars, no_bar_le_decision, no_anchor, contract_roll,
// decision_print_stale_<n>s), decide_charteris()'s 'tsx_holiday' carry, and
// the shared gge_mt_signal.decide() threshold / degraded_carry reasons.
// Pure lookup — unit tested without a running signal service. Never throws on
// null m_0840 / anchor_price / threshold_bps.
import type { GatedFuturesSignal } from '../../api/client'
import { NUMERIC_REASONS, formatSignedBp } from '../japan/japanReasons'

type ReasonText = { text: string; tone: 'ok' | 'amber' }

/** Why there is no live SI move — every sentence ends "no live signal". */
function noLiveSignal(code: string | null | undefined): string {
  if (code === 'no_bars' || code === 'no_bar_le_decision') return 'No silver futures bars before 08:40 — no live signal'
  if (code === 'no_anchor') return 'No NY-close silver anchor — no live signal'
  if (code === 'contract_roll') return 'Silver contract rolled between anchor and 08:40 — no live signal'
  const stale = code?.match(/^decision_print_stale_(\d+)s$/)
  if (stale) return `Last silver futures print ${stale[1]}s old — no live signal`
  return 'Silver move unavailable — no live signal'
}

const NO_LIVE_SIGNAL_CODES = new Set(['no_bars', 'no_bar_le_decision', 'no_anchor', 'contract_roll'])

export function describeCharterisReason(s: GatedFuturesSignal): ReasonText {
  const reason = s.reason

  // TSX holiday on the anchor session: the producer carries yesterday's
  // decision and does NOT mark it degraded — a normal state, so tone 'ok'.
  if (reason === 'tsx_holiday') {
    const carried = s.decision ? `yesterday's decision (${s.decision}) carried` : "yesterday's decision carried"
    return { text: `TSX holiday on the anchor session — ${carried}; no new deal`, tone: 'ok' }
  }

  if (reason && NUMERIC_REASONS.has(reason)) {
    if (s.m_0840 == null || s.threshold_bps == null) return { text: 'SI move unavailable', tone: 'ok' }
    const x = formatSignedBp(s.m_0840)
    const t = Math.round(s.threshold_bps)
    switch (reason) {
      case 'm_ge_threshold':
        return { text: `SI ${x} bp since NY close — at or above the ${t} bp threshold`, tone: 'ok' }
      case 'm_below_threshold':
        return { text: `SI ${x} bp since NY close — below the ${t} bp threshold`, tone: 'ok' }
      case 'm_above_buy_threshold':
        return { text: `SI ${x} bp since NY close — above the buy threshold`, tone: 'ok' }
      case 'm_below_sell_threshold':
        return { text: `SI ${x} bp since NY close — below the sell threshold`, tone: 'ok' }
      case 'dead_zone_hold_buy':
        return { text: `SI ${x} bp since NY close — inside the dead zone, staying invested`, tone: 'ok' }
      default:
        return { text: `SI ${x} bp since NY close — inside the dead zone, staying in cash`, tone: 'ok' }
    }
  }

  // The producer publishes degraded_carry* with the underlying cause in m_reason.
  if (reason === 'degraded_carry') {
    return { text: `${noLiveSignal(s.m_reason)}; yesterday's stance carried`, tone: 'amber' }
  }
  if (reason === 'degraded_carry_bootstrap') {
    return { text: `${noLiveSignal(s.m_reason)}; no previous stance, defaulted to BUY`, tone: 'amber' }
  }
  // The m_reason codes themselves, should one ever arrive as the reason.
  if (reason && NO_LIVE_SIGNAL_CODES.has(reason)) {
    return { text: noLiveSignal(reason), tone: 'amber' }
  }

  return { text: reason || 'SI move unavailable', tone: 'amber' }
}
